import isEqual from 'lodash/isEqual';
import { useEffect, useMemo } from 'react';
import { Col, Form, Row } from 'react-bootstrap';
import { Control, useForm, useWatch } from 'react-hook-form';
import { BsSortDown, BsSortUp } from 'react-icons/bs';
import { ControlProps, components } from 'react-select';
import { cleanAndSortOptions } from '../../../utils/forms';
import { AutoSuggest } from '../AutoSuggest';
import { FilterDropdown } from '../FilterDropDown';
import { SelectController } from '../SelectController';
import {
  SORT_OPTIONS_HCO,
  SORT_OPTIONS_PROVIDER,
  SPECIALTIES_OPTIONS,
  defaultFilter,
} from './constants';
import { ConnectionRequestFilterState } from './models';

export const ConnectionRequestsFilters = ({
  filters,
  onApply,
  isProvider,
  facilitiesOptions,
  opportunitiesOptions,
  searchOptions,
  searchPlaceholder,
}: {
  filters: ConnectionRequestFilterState;
  onApply: (filter: ConnectionRequestFilterState) => void;
  isProvider: boolean;
  facilitiesOptions: Record<string, unknown>[];
  opportunitiesOptions: Record<string, unknown>[];
  searchOptions: string[];
  searchPlaceholder: string;
}) => {
  const { reset, control, getValues, setValue, formState } = useForm<ConnectionRequestFilterState>({
    defaultValues: filters,
  });

  useEffect(() => {
    reset(filters);
  }, [filters, reset]);

  // TODO: should be encapsulated in a separate react hook to avoid code duplication
  const filtersFormValues = useWatch({
    control,
  });

  const clearFilters = () => {
    const clearedValues = {
      ...defaultFilter,
      search: getValues().search, // Preserve search
      sortBy: getValues().sortBy, // Preserve sorting
      sortDirection: getValues().sortDirection,
    };

    reset(clearedValues);
    applyFilters();
  };

  const applyFilters = () => {
    if (onApply && !formState.isSubmitting) {
      const values = getValues();
      const filters = {
        ...values,
        facility: values.facility?.length ? values.facility : [],
        opportunity: values.opportunity?.length ? values.opportunity : [],
      };
      onApply(filters);
    }
  };

  const onToggle = () => {
    if (filters) {
      reset(filters);
    }
  };

  const changeSortDirection = (event) => {
    event.preventDefault();
    event.stopPropagation();
    const sortDirection = getValues().sortDirection === 'asc' ? 'desc' : 'asc';
    setValue('sortDirection', sortDirection);
    applyFilters();
  };

  const sortOptions = useMemo(() => {
    return isProvider ? SORT_OPTIONS_PROVIDER : SORT_OPTIONS_HCO;
  }, [isProvider]);

  const filtersCount = useMemo(() => {
    const values = filtersFormValues || {};

    return Object.keys(values).filter((key) => {
      // Skip certain keys that shouldn't count as filters
      if (['sortBy', 'sortDirection', 'ordering', 'search'].includes(key)) {
        return false;
      }

      const value = values[key];
      if (Array.isArray(value)) {
        return value.length > 0;
      }
      return value !== '' && value !== null && value !== undefined;
    }).length;
  }, [filtersFormValues]);

  const isDirty = useMemo(() => {
    if (!filtersFormValues) return false;
    return !isEqual(filtersFormValues, filters);
  }, [filtersFormValues, filters]);

  const uniqueFacilitiesOptions = cleanAndSortOptions(facilitiesOptions, 'id', 'name');
  const uniqueOpportunitiesOptions = cleanAndSortOptions(opportunitiesOptions, 'id', 'title');
  const uniqueSpecialtiesOptions = cleanAndSortOptions(SPECIALTIES_OPTIONS);

  return (
    <>
      <Row className="connection-requests-filters">
        <Col xs={12} lg={5} xl={5}>
          <AutoSuggest
            value={filters.search}
            placeholder={searchPlaceholder}
            options={searchOptions}
            onChange={(value) => {
              setValue('search', value);
              applyFilters();
            }}
          />
        </Col>
        <Col lg="auto" xs={12} xl={3} className="p-lg-0 py-1">
          <FilterDropdown
            isDirty={isDirty}
            filtersCount={filtersCount}
            onClear={clearFilters}
            onApply={applyFilters}
            onToggle={onToggle}
          >
            {!isProvider && (
              <Form.Group className="mb-3">
                <Form.Label className="fw-bold">Specialties</Form.Label>
                <SelectController
                  menuPortalTarget={
                    document.getElementById('filter-dropdown-portal') as HTMLElement
                  }
                  control={control as unknown as Control<Record<string, unknown>>}
                  name="specialties"
                  placeholder="Choose one or more..."
                  options={uniqueSpecialtiesOptions}
                />
              </Form.Group>
            )}
            <Form.Group className="mb-3">
              <Form.Label className="fw-bold" htmlFor="opportunity">
                Opportunities
              </Form.Label>
              <SelectController
                menuPortalTarget={document.getElementById('filter-dropdown-portal') as HTMLElement}
                control={control as unknown as Control<Record<string, unknown>>}
                name="opportunity"
                placeholder="Choose one or more..."
                options={uniqueOpportunitiesOptions.map((opportunity) => ({
                  value: opportunity.id,
                  label: opportunity.title,
                }))}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label className="fw-bold" htmlFor="facility">
                Facilities
              </Form.Label>
              <SelectController
                menuPortalTarget={document.getElementById('filter-dropdown-portal') as HTMLElement}
                control={control as unknown as Control<Record<string, unknown>>}
                name="facility"
                placeholder="Choose one or more..."
                options={uniqueFacilitiesOptions.map((facility) => ({
                  value: facility.id,
                  label: facility.name,
                }))}
              />
            </Form.Group>
          </FilterDropdown>
        </Col>
        <Col xs={12} lg={4} xl={4}>
          <SelectController
            control={control as unknown as Control<Record<string, unknown>>}
            name="sortBy"
            placeholder="Sort by"
            isMulti={false}
            options={sortOptions}
            components={{
              Control: ({ children, ...props }: ControlProps) => (
                <components.Control {...props}>
                  <span className="sorting-btn">
                    {getValues().sortDirection === 'asc' ? (
                      <BsSortUp onMouseDown={changeSortDirection} size={16} />
                    ) : (
                      <BsSortDown onMouseDown={changeSortDirection} size={16} />
                    )}
                  </span>
                  {children}
                </components.Control>
              ),
            }}
            onChange={() => {
              applyFilters();
            }}
          />
        </Col>
      </Row>
    </>
  );
};
